import type { BadgeProps } from '@/components/ui/badge.js';

/** One choice of a select: the value on the wire and the words a person reads. */
export interface Option {
  value: string;
  label: string;
}

/** The sources the tracker reads, then the tasks the user added, in the order the filter offers them. */
export const TASK_SOURCES = [
  'teams',
  'outlook',
  'jira',
  'confluence',
  'manual',
] as const;

const SOURCE_LABELS: Record<string, string> = {
  teams: 'Teams',
  outlook: 'Outlook',
  jira: 'Jira',
  confluence: 'Confluence',
  manual: 'Added by you',
};

const PRIORITY_LOOK: Record<
  string,
  { label: string; variant: NonNullable<BadgeProps['variant']> }
> = {
  high: { label: 'High', variant: 'warning' },
  medium: { label: 'Medium', variant: 'secondary' },
  low: { label: 'Low', variant: 'outline' },
};

/** The statuses a person can pick, in the order work moves through them. */
export const STATUS_OPTIONS: readonly Option[] = [
  { value: 'new', label: 'New' },
  { value: 'reopened', label: 'Reopened' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'wont_do', label: "Won't do" },
  { value: 'done', label: 'Done' },
];

/** The priorities, highest first, as the filter offers them. */
export const PRIORITY_OPTIONS: readonly Option[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/** The name of a task's source; one this build does not know shows as sent. */
export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/** The words and tone of a priority; one this build does not know shows as sent, in outline. */
export function priorityLook(priority: string): {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
} {
  return PRIORITY_LOOK[priority] ?? { label: priority, variant: 'outline' };
}

/**
 * The statuses to pick from. A current status this build does not know joins them as sent, because
 * a select shows nothing for a value it has no option for.
 */
export function statusOptions(current: string): readonly Option[] {
  return STATUS_OPTIONS.some((option) => option.value === current)
    ? STATUS_OPTIONS
    : [...STATUS_OPTIONS, { value: current, label: current }];
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** How long a task took, in its two largest units: `3 h 20 min`, `2 d 4 h`, or a dash for none. */
export function formatTimeToComplete(durationMs: number | null): string {
  if (durationMs === null) return '—';
  const totalMinutes = Math.round(durationMs / 60_000);
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0)
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return minutes > 0 ? `${minutes} min` : '< 1 min';
}
